import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { RoundModel } from '@/models/RoundModels';
import { CourseModel } from '@/models/CourseModels';
import usePageTitle from '@/hooks/usePageTitle';
import Modal from '../components/Modal';
import LoadingSpinner, { ButtonSpinner } from '../components/LoadingSpinner';
import { resolveType } from '@/services/ServiceContainer';
import { CourseService } from '@/services/CourseService';
import { RoundService } from '@/services/RoundService';
import { deleteField, Timestamp } from 'firebase/firestore';
import { LeagueResultService } from '@/services/LeagueResultService';
import { calcStrokesGiven, calcNetScore } from '../utils/handicapUtils';
import { getScoreColors } from '../utils/themeUtils';
import { LeagueService } from '@/services/LeagueService';


const SCORECARD_TUTORIAL_KEY = 'SCORECARD_TUTORIAL_SHOWN';

const RoundScorecard: React.FC = () => {
    //const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const courseId = params.courseId;
    const roundId = params.roundId;
    const { theme } = useTheme();
    const { user } = useAuth();
    const [courseModel, setCourseModel] = useState<CourseModel | null>(null);
    const [roundModel, setRoundModel] = useState<RoundModel | null>(null);
    const [loading, setLoading] = useState(true);
    const [completingRound, setCompletingRound] = useState(false);
    const [error, setError] = useState('');
    const [deletingRound, setDeletingRound] = useState(false);
    const [editingRound, setEditingRound] = useState(false);
    const [, setCurrentHole] = useState<number>(1);
    const [showTutorial, setShowTutorial] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const [statsExpanderOpen, setStatsExpanderOpen] = useState(false);
    const scorecardRef = useRef<HTMLDivElement>(null);
    const tutorialTimeoutRef = useRef<NodeJS.Timeout>();
    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        type: 'info' | 'warning' | 'error';
        onConfirm?: () => void;
        confirmText?: string;
        isLoading?: boolean;
    }>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        confirmText: 'Confirm',
        isLoading: false
    });
    const [leagueName, setLeagueName] = useState<string | null>(null);

    // Add keyboard event handling for modal
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!modalConfig.isOpen) return;

            if (event.key === 'Escape') {
                // Run Cancel button logic
                setModalConfig(prev => ({ ...prev, isOpen: false }));
            } else if (event.key === 'Enter') {
                // Run Confirm button logic
                if (modalConfig.onConfirm) {
                    modalConfig.onConfirm();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [modalConfig.isOpen, modalConfig.onConfirm]);

    // Set page title
    usePageTitle('Scorecard');

    useEffect(() => {
        const loadData = async () => {
            if (!courseId || !roundId) {
                setError('Missing course or round information');
                setLoading(false);
                return;
            }

            try {
                // Load course data
                const courseService = resolveType(CourseService);
                const courseModel = await courseService.get(courseId);
                if (courseModel) {
                    setCourseModel(courseModel);
                } else {
                    setError('Course not found');
                }

                const roundService = resolveType(RoundService);
                const round = await roundService.get(roundId);
                if (round) {
                    setRoundModel(round);
                } else {
                    setError('Round not found');
                    setLoading(false);
                    return;
                }

                // Find the last hole played by the current user
                const userData = round.users[user?.uid || ''];
                if (userData) {
                    const lastPlayedHole = Math.max(...userData.holeStats
                        .filter(stat => stat.grossScore !== null)
                        .map(stat => stat.hole));
                    setCurrentHole(lastPlayedHole + 1);
                }
            } catch (error) {
                console.error('Error loading data:', error);
                setError('Failed to load round data');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [courseId, roundId, user]);

    useEffect(() => {
        const statsExpanderOpen = localStorage.getItem('holeEntryStatsExpanderOpen') === 'true';
        if (statsExpanderOpen) {
            setStatsExpanderOpen(true);
        }

        const hasSeenTutorial = localStorage.getItem(SCORECARD_TUTORIAL_KEY);
        if (!hasSeenTutorial) {
            // Small delay to ensure the DOM is ready
            tutorialTimeoutRef.current = setTimeout(() => {
                setShowTutorial(true);
            }, 500);
        }

        return () => {
            if (tutorialTimeoutRef.current) {
                clearTimeout(tutorialTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!showTutorial || !scorecardRef.current) return;

        const handleScroll = () => {
            const scrollLeft = scorecardRef.current?.scrollLeft;
            if (typeof scrollLeft === 'number' && scrollLeft > 0) {
                console.log('disabling scroll tutorial');
                localStorage.setItem('SCORECARD_TUTORIAL_SHOWN', 'true');
                setShowTutorial(false);
            }
        };

        const scrollContainer = scorecardRef.current;
        scrollContainer.addEventListener('scroll', handleScroll);

        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
        };
    }, [showTutorial]);

    useEffect(() => {
        const checkScorecardVisibility = () => {
            if (scorecardRef.current) {
                const el = scorecardRef.current;
                // If no horizontal scroll is needed, the whole scorecard is visible
                if (el.scrollWidth <= el.clientWidth) {
                    setShowTutorial(false);
                    localStorage.setItem(SCORECARD_TUTORIAL_KEY, 'true');
                }
            }
        };
        // Use a timeout to ensure DOM is rendered
        const timeout = setTimeout(checkScorecardVisibility, 100);
        window.addEventListener('resize', checkScorecardVisibility);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', checkScorecardVisibility);
        };
    }, [roundModel, courseModel]);

    useEffect(() => {
        if (showTutorial) {
            const timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        setShowTutorial(false);
                        localStorage.setItem(SCORECARD_TUTORIAL_KEY, 'true');
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [showTutorial]);

    useEffect(() => {
        // Fetch league name if this is a league round
        if (roundModel && roundModel.setup && roundModel.setup.type === 'league' && roundModel.setup.league?.id) {
            const leagueService = resolveType(LeagueService) as LeagueService;
            leagueService.get(roundModel.setup.league.id).then((league: import('@/models/LeagueModels').LeagueModel | null) => {
                if (league) setLeagueName(league.name);
            });
        }
    }, [courseId, roundId, user, roundModel]);

    const handleDeleteRound = async () => {
        if (!roundId || !user) return;

        const isLastUser = Object.keys(roundModel?.users || {}).length <= 1;
        const message = isLastUser
            ? 'Are you sure you want to delete this round? This action cannot be undone.'
            : 'Are you sure you want to leave this round?';

        setModalConfig({
            isOpen: true,
            title: isLastUser ? 'Delete Round' : 'Leave Round',
            message,
            type: 'error',
            confirmText: isLastUser ? 'Delete' : 'Leave',
            isLoading: false,
            onConfirm: async () => {
                try {
                    setDeletingRound(true);
                    setModalConfig(prev => ({ ...prev, isLoading: true }));
                    let leagueResultsDocId = null;
                    let memberKey = null;
                    if (roundModel) {
                        if (roundModel.setup && roundModel.setup.type === 'league' && roundModel.setup.league && roundModel.setup.league.id && roundModel.setup.league.dayId) {
                            leagueResultsDocId = `${roundModel.setup.league.id}_${roundModel.setup.league.dayId}`;
                            // Prefer encodedEmail, fallback to uid
                            memberKey = roundModel.users?.[user.uid]?.encodedEmail || user.uid;
                        }

                        const roundService = resolveType(RoundService);

                        if (isLastUser) {
                            // Delete the entire round if this is the last user
                            await roundService.delete(roundModel.id, user.uid);
                        } else {
                            // Remove just this user from the round
                            await roundService.update(roundModel.id, user.uid, {
                                [`users.${user.uid}`]: deleteField()
                            }, null);
                        }

                        // --- Remove user's holeStats from leagueResults ---
                        if (leagueResultsDocId && memberKey) {
                            const leagueResultService = resolveType(LeagueResultService);
                            const leagueResultModel = await leagueResultService.get(leagueResultsDocId);
                            if (leagueResultModel) {
                                await leagueResultService.update(leagueResultsDocId, {
                                    [`members.${memberKey}.holeStats`]: deleteField()
                                });
                            }
                        }
                    }

                    // Navigate to dashboard
                    navigate('/dashboard');
                } catch (error) {
                    console.error('Error deleting round:', error);
                    setError('Failed to delete round');
                    setDeletingRound(false);
                    setModalConfig(prev => ({ ...prev, isLoading: false }));
                }
            }
        });
    };

    const handleCompleteRound = async () => {
        if (!roundId || !user || !courseModel) return;

        if (!roundModel) {
            console.error('Round data is missing');
            return;
        }

        // Check if there are any holes with no score
        const userData = roundModel.users[user.uid];
        if (userData) {
            const holesWithScore = userData.holeStats.filter(stat => (stat.grossScore || 0) > 0);
            if (roundModel.setup.holesToPlay === 'all18') {
                if (holesWithScore.length < 18) {
                    setModalConfig({
                        isOpen: true,
                        title: 'Missing Scores',
                        message: `You have ${18 - holesWithScore.length} hole(s) with no score. Are you sure you want to complete this round?`,
                        type: 'warning',
                        onConfirm: () => {
                            setModalConfig(prev => ({ ...prev, isOpen: false }));
                            checkStatsAndComplete();
                        }
                    });
                    return;
                }
            } else {
                if (holesWithScore.length < 9) {
                    setModalConfig({
                        isOpen: true,
                        title: 'Missing Scores',
                        message: `You have ${9 - holesWithScore.length} hole(s) with no score. Are you sure you want to complete this round?`,
                        type: 'warning',
                        onConfirm: () => {
                            setModalConfig(prev => ({ ...prev, isOpen: false }));
                            checkStatsAndComplete();
                        }
                    });
                    return;
                }
            }

            // Check for extraordinary scores (hole-in-one, albatross, eagle)
            const extraordinaryScores: { hole: number; score: number; par: number; type: string }[] = [];
            userData.holeStats.forEach(stat => {
                if (!stat.grossScore || !stat.par) return;
                const scoreNum = stat.grossScore;
                const par = stat.par;
                const relativeToPar = scoreNum - par;
                if (scoreNum === 1) {
                    extraordinaryScores.push({ hole: stat.hole, score: scoreNum, par, type: 'Hole in One' });
                } else if (relativeToPar === -3) {
                    extraordinaryScores.push({ hole: stat.hole, score: scoreNum, par, type: 'Albatross' });
                } else if (relativeToPar === -2) {
                    extraordinaryScores.push({ hole: stat.hole, score: scoreNum, par, type: 'Eagle' });
                }
            });
            if (extraordinaryScores.length > 0) {
                const message = `Extraordinary scores detected:\n\n${extraordinaryScores
                    .map(score => `Hole ${score.hole} (Par ${score.par}): ${score.score} - ${score.type}`)
                    .join('\n')}\n\nContinue?`;
                setModalConfig({
                    isOpen: true,
                    title: 'Extraordinary Scores',
                    message,
                    type: 'warning',
                    onConfirm: () => {
                        setModalConfig(prev => ({ ...prev, isOpen: false }));
                        checkStatsAndComplete();
                    }
                });
                return;
            }
        }

        checkStatsAndComplete();
    };

    const checkStatsAndComplete = async () => {
        if (!roundId || !user || !courseModel || !roundModel) return;

        const userData = roundModel.users[user.uid];
        if (userData) {
            // Check if stats tracking is enabled
            const statsExpanderEnabled = localStorage.getItem('holeEntryStatsExpanderOpen') === 'true';

            // Only check for missing stats if stats tracking is enabled
            if (statsExpanderEnabled) {
                const holesWithMissingStats = userData.holeStats.filter(stat => {
                    // Only check holes that have scores
                    if (!stat.grossScore || stat.grossScore <= 0) return false;

                    // Get the hole's par
                    const hole = courseModel.scorecard.holes.find(h => h.number === stat.hole);
                    const isPar3 = hole?.par === 3;

                    // Check if any of the stats are missing
                    // For par 3 holes, don't check fairwayHit
                    return (!isPar3 && stat.fairwayHit === undefined) ||
                        stat.greenInRegulation === undefined ||
                        stat.chips === undefined ||
                        stat.putts === undefined;
                });

                if (holesWithMissingStats.length > 0) {
                    setModalConfig({
                        isOpen: true,
                        title: 'Missing Stats',
                        message: `You have ${holesWithMissingStats.length} hole(s) with missing stats (${holesWithMissingStats.some(stat => {
                            const hole = courseModel.scorecard.holes.find(h => h.number === stat.hole);
                            const isPar3 = hole?.par === 3;
                            return !isPar3 && stat.fairwayHit === undefined;
                        }) ? 'fairway hit, ' : ''}GIR, chips, or putts). Are you sure you want to complete this round?`,
                        type: 'warning',
                        onConfirm: () => {
                            setModalConfig(prev => ({ ...prev, isOpen: false }));
                            completeRound();
                        }
                    });
                    return;
                }
            }
        }

        completeRound();
    };

    const completeRound = async () => {
        if (!roundId || !user || !courseModel || !roundModel) return;

        try {
            setCompletingRound(true);

            const roundService = resolveType(RoundService);
            await roundService.complete(roundModel, user.uid, courseModel);

            // Navigate to dashboard
            navigate('/dashboard');
        } catch (error) {
            console.error('Error finishing round:', error);
            setError('Failed to finish round: ' + error);
            setCompletingRound(false);
        }
    };

    const handleReturnToHoleEntry = async () => {
        if (!roundId || !user || !courseId) return;

        try {
            setEditingRound(true);
            // Update the round status to active
            if (roundModel?.status !== 'active') {
                const roundService = resolveType(RoundService);
                await roundService.update(roundId, user.uid, {
                    status: 'active',
                    updatedAt: Timestamp.now()
                }, null);
            }

            // Get entry mode from localStorage
            const entryMode = localStorage.getItem('entryMode') || 'holeByHole';

            if (entryMode === 'scoreSummary') {
                navigate(`/scores-only-entry/${courseId}/${roundId}`);
            } else {
                // Find the last hole with a score
                const userData = roundModel?.users[user.uid];
                if (!userData) return;

                const holesWithScores = userData.holeStats
                    .filter(stat => {
                        // Filter by holes to play setting
                        if (roundModel?.setup.holesToPlay === 'front9') return stat.hole <= 9;
                        if (roundModel?.setup.holesToPlay === 'back9') return stat.hole > 9;
                        return true;
                    })
                    .filter(stat => stat.grossScore !== null && stat.grossScore > 0);

                // If no holes have scores, start at the beginning
                const lastHoleWithScore = holesWithScores.length > 0
                    ? holesWithScores[holesWithScores.length - 1].hole
                    : (roundModel?.setup.holesToPlay === 'back9' ? 10 : 1);

                navigate(`/hole-entry/${courseId}/${roundId}/${lastHoleWithScore}`);
            }
        } catch (error) {
            console.error('Error updating round status:', error);
            setError('Failed to update round status');
            setEditingRound(false);
        }
    };

    const handleScoreClick = (holeNumber: number) => {
        // Don't navigate if the round is completed or tutorial is showing or the user isn't in the round (i.e. another user shared this link with them)
        if (roundModel?.status === 'completed' || showTutorial || !canUserInteract()) {
            return;
        }

        // Get entry mode from localStorage
        const entryMode = localStorage.getItem('entryMode') || 'holeByHole';

        if (entryMode === 'scoreSummary') {
            navigate(`/scores-only-entry/${courseId}/${roundId}`);
        } else {
            // Navigate to hole-entry with the selected hole
            navigate('/hole-entry', {
                state: {
                    courseId,
                    roundId,
                    roundSetup: roundModel?.setup,
                    selectedHole: holeNumber
                }
            });
        }
    };

    // const getTeeColorForHole = (hole: number, teeName: string, yards?: number) => {
    //     return getTeeColors(teeName, {
    //         currentHole: hole,
    //         currentYards: yards,
    //         course: course
    //     });
    // };

    const firstUser = roundModel && roundModel.users ? Object.values(roundModel.users)[0] : null;

    // Helper function to check if user can interact with the scorecard
    const canUserInteract = () => {
        return user && roundModel?.users && roundModel.users[user.uid] && roundModel?.status !== 'completed';
    };

    if (loading) {
        return (
            <div style={{ padding: theme.spacing.MEDIUM, textAlign: 'center' }}>
                Loading...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: theme.spacing.MEDIUM, textAlign: 'center', color: theme.colors.ERROR }}>
                {error}
            </div>
        );
    }

    if (!courseModel || !roundModel) {
        return (
            <div style={{ padding: theme.spacing.MEDIUM, textAlign: 'center' }}>
                Round or course data not found
            </div>
        );
    }

    return (
        <>
            {completingRound && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: theme.colors.BACKGROUND,
                        padding: theme.spacing.LARGE,
                        borderRadius: theme.borderRadius.MEDIUM,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: theme.spacing.MEDIUM
                    }}>
                        <LoadingSpinner />
                        <div style={{ color: theme.colors.TEXT }}>Submitting round...</div>
                    </div>
                </div>
            )}

            <div style={{ padding: theme.spacing.MEDIUM }}>
                <div style={{
                    marginBottom: theme.spacing.LARGE,
                    padding: theme.spacing.MEDIUM,
                    backgroundColor: theme.colors.SURFACE,
                    borderRadius: theme.borderRadius.MEDIUM,
                    border: `1px solid ${theme.colors.BORDER}`
                }}>
                    {/* League name at the top of the card if present */}
                    {roundModel && roundModel.setup && roundModel.setup.type === 'league' && leagueName && (
                        <div style={{
                            textAlign: 'center',
                            color: theme.colors.TEXT,
                            fontWeight: 'bold',
                            fontSize: '1.3em',
                            marginBottom: theme.spacing.SMALL
                        }}>
                            {leagueName}
                        </div>
                    )}
                    <div style={{
                        color: theme.colors.TEXT,
                        marginBottom: theme.spacing.SMALL,
                        fontSize: '1.1em',
                        fontWeight: 400, // normal
                        textAlign: 'center'
                    }}>
                        {courseModel?.name}
                    </div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: theme.spacing.SMALL
                    }}>
                        <div style={{
                            color: theme.colors.TEXT,
                            fontSize: '1.2em',
                            fontWeight: 'bold'
                        }}>
                            {roundModel.setup.holesToPlay === 'all18' ? '18 Holes' :
                                roundModel.setup.holesToPlay === 'front9' ? 'Front 9' : 'Back 9'}
                        </div>

                        <div style={{
                            color: theme.colors.TEXT,
                            fontSize: '1.2em',
                            fontWeight: 'bold'
                        }}>
                            {roundModel.setup.type === 'league' && roundModel.setup.league?.dayId ? (
                                (() => {
                                    // Format YYYY-MM-DD as M/D
                                    const d = (roundModel.setup.league.dayId || '').replace('day_', '').replace(/_/gi, '-');
                                    const dateObj = new Date(d + 'T00:00:00');
                                    return `${dateObj.getMonth() + 1}/${dateObj.getDate()}`;
                                })()
                            ) : (
                                roundModel.startedAt ? (
                                    typeof roundModel.startedAt.toDate === 'function'
                                        ? roundModel.startedAt.toDate().toLocaleDateString()
                                        : new Date(roundModel.startedAt.seconds * 1000).toLocaleDateString()
                                ) : 'Date not available'
                            )}
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: theme.spacing.SMALL
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.MEDIUM
                        }}>
                            <span style={{
                                color: theme.colors.TEXT_SECONDARY,
                                fontSize: '1em',
                                marginRight: theme.spacing.SMALL
                            }}>
                                Hcp:
                            </span>
                            <span style={{
                                color: theme.colors.TEXT,
                                fontSize: '1em',
                                fontWeight: 'bold'
                            }}>
                                {Object.values(roundModel.users)[0]?.handicap || 'N/A'}
                            </span>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            {roundModel.status === 'completed' && (
                                <span style={{ fontSize: '1em' }}>✅</span>
                            )}
                            <span style={{
                                color: theme.colors.TEXT,
                                fontWeight: 'bold',
                                fontSize: '1em',
                                textTransform: 'capitalize'
                            }}>
                                {roundModel.status === 'active' ? 'In Progress' : roundModel.status === 'completed' ? 'Submitted' : roundModel.status || 'Unknown'}
                            </span>
                        </div>
                    </div>
                </div>


                {/* Round Stats Summary */}
                {Object.values(roundModel.users)[0]?.holeStats?.some((stat: any) =>
                    (stat.fairwayHit !== undefined && stat.fairwayHit !== null) ||
                    (stat.greenInRegulation !== undefined && stat.greenInRegulation !== null) ||
                    (stat.chips !== undefined && stat.chips !== null) ||
                    (stat.putts !== undefined && stat.putts !== null) ||
                    (stat.penalties !== undefined && stat.penalties !== null)
                ) && (
                        <div style={{
                            marginBottom: theme.spacing.LARGE,
                            padding: theme.spacing.MEDIUM,
                            backgroundColor: theme.colors.SURFACE,
                            borderRadius: theme.borderRadius.MEDIUM,
                            border: `1px solid ${theme.colors.BORDER}`
                        }}>
                            <h3 style={{
                                color: theme.colors.TEXT,
                                marginBottom: theme.spacing.MEDIUM,
                                fontSize: '1.2em',
                                fontWeight: 'bold'
                            }}>
                                Round Stats
                            </h3>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: theme.spacing.MEDIUM
                            }}>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <span style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontSize: '0.9em'
                                    }}>Fairway</span>
                                    <span style={{
                                        color: theme.colors.TEXT,
                                        fontWeight: 'bold'
                                    }}>
                                        {Object.values(roundModel.users)[0]?.holeStats?.some((stat: any) => stat.fairwayHit !== undefined && stat.fairwayHit !== null)
                                            ? Object.values(roundModel.users)[0]?.holeStats?.filter((stat: any) => stat.fairwayHit === true).length || '0'
                                            : '-'
                                        }
                                    </span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <span style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontSize: '0.9em'
                                    }}>GIR</span>
                                    <span style={{
                                        color: theme.colors.TEXT,
                                        fontWeight: 'bold'
                                    }}>
                                        {Object.values(roundModel.users)[0]?.holeStats?.some((stat: any) => stat.greenInRegulation !== undefined && stat.greenInRegulation !== null)
                                            ? Object.values(roundModel.users)[0]?.holeStats?.filter((stat: any) => stat.greenInRegulation === true).length || '0'
                                            : '-'
                                        }
                                    </span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <span style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontSize: '0.9em'
                                    }}>Chips</span>
                                    <span style={{
                                        color: theme.colors.TEXT,
                                        fontWeight: 'bold'
                                    }}>
                                        {Object.values(roundModel.users)[0]?.holeStats?.some((stat: any) => stat.chips !== undefined && stat.chips !== null)
                                            ? Object.values(roundModel.users)[0]?.holeStats?.reduce((total: number, stat: any) => total + (stat.chips || 0), 0) || '0'
                                            : '-'
                                        }
                                    </span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <span style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontSize: '0.9em'
                                    }}>Putts</span>
                                    <span style={{
                                        color: theme.colors.TEXT,
                                        fontWeight: 'bold'
                                    }}>
                                        {Object.values(roundModel.users)[0]?.holeStats?.some((stat: any) => stat.putts !== undefined && stat.putts !== null)
                                            ? Object.values(roundModel.users)[0]?.holeStats?.reduce((total: number, stat: any) => total + (stat.putts || 0), 0) || '0'
                                            : '-'
                                        }
                                    </span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <span style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontSize: '0.9em'
                                    }}>Penalties</span>
                                    <span style={{
                                        color: theme.colors.TEXT,
                                        fontWeight: 'bold'
                                    }}>
                                        {Object.values(roundModel.users)[0]?.holeStats?.some((stat: any) => stat.penalties !== undefined && stat.penalties !== null)
                                            ? Object.values(roundModel.users)[0]?.holeStats?.reduce((total: number, stat: any) => total + (stat.penalties || 0), 0) || '0'
                                            : '-'
                                        }
                                    </span>
                                </div>
                            </div>
                            {roundModel.status === 'completed' && roundModel.completedAt && (
                                <div style={{
                                    marginTop: theme.spacing.MEDIUM,
                                    paddingTop: theme.spacing.MEDIUM,
                                    borderTop: `1px solid ${theme.colors.BORDER}`,
                                    textAlign: 'center'
                                }}>
                                    <div style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        fontSize: '0.9em',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px'
                                    }}>
                                        <span style={{ fontSize: '1em' }}>✅</span>
                                        {(() => {
                                            const completedAt = roundModel.completedAt;
                                            if (!completedAt) return 'Submitted';
                                            let dateObj;
                                            if (typeof completedAt.toDate === 'function') {
                                                dateObj = completedAt.toDate();
                                            } else if (completedAt.seconds) {
                                                dateObj = new Date(completedAt.seconds * 1000);
                                            } else {
                                                return 'Submitted';
                                            }
                                            return `Submitted ${dateObj.toLocaleString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true
                                            })}`;
                                        })()}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                {/* Only show action buttons if the authenticated user is a participant in this round */}
                {user && roundModel?.users && roundModel.users[user.uid] && (
                    <div style={{
                        display: 'flex',
                        gap: theme.spacing.MEDIUM,
                        justifyContent: 'center',
                        marginBottom: theme.spacing.MEDIUM,
                        visibility: showTutorial ? 'hidden' : 'visible'
                    }}>
                        <button
                            onClick={handleDeleteRound}
                            style={{
                                padding: theme.spacing.MEDIUM,
                                backgroundColor: theme.colors.ERROR,
                                color: theme.colors.WHITE,
                                border: 'none',
                                borderRadius: theme.borderRadius.MEDIUM,
                                cursor: deletingRound ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '80px',
                                height: '54px',
                                opacity: deletingRound ? 0.7 : 1,
                                fontSize: '1rem',
                                fontWeight: '600'
                            }}
                            title="Delete Round"
                            disabled={deletingRound}
                        >
                            {deletingRound ? (
                                <ButtonSpinner size={24} color={theme.colors.WHITE} />
                            ) : (
                                'Delete'
                            )}
                        </button>
                        <button
                            onClick={handleReturnToHoleEntry}
                            style={{
                                padding: theme.spacing.MEDIUM,
                                backgroundColor: theme.colors.BUTTON_SECONDARY_BG,
                                color: theme.colors.BUTTON_SECONDARY_TEXT,
                                border: 'none',
                                borderRadius: theme.borderRadius.MEDIUM,
                                cursor: editingRound ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '80px',
                                height: '54px',
                                opacity: editingRound ? 0.7 : 1,
                                fontSize: '1rem',
                                fontWeight: '600'
                            }}
                            title="Continue Round"
                            disabled={editingRound}
                        >
                            {editingRound ? (
                                <ButtonSpinner size={24} color={theme.colors.BACKGROUND} />
                            ) : (
                                'Edit'
                            )}
                        </button>
                        {roundModel?.status !== 'completed' && (
                            <>
                                <button
                                    onClick={handleCompleteRound}
                                    disabled={completingRound}
                                    style={{
                                        padding: theme.spacing.MEDIUM,
                                        backgroundColor: theme.colors.PRIMARY,
                                        color: theme.colors.WHITE,
                                        border: 'none',
                                        borderRadius: theme.borderRadius.MEDIUM,
                                        cursor: completingRound ? 'not-allowed' : 'pointer',
                                        fontSize: '1.25em',
                                        width: '120px',
                                        height: '54px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: theme.spacing.SMALL
                                    }}
                                >
                                    {completingRound ? (
                                        <>
                                            <div style={{
                                                width: '20px',
                                                height: '20px',
                                                border: `2px solid ${theme.colors.BACKGROUND}`,
                                                borderTop: '2px solid transparent',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }} />
                                            <style>
                                                {`
                                                    @keyframes spin {
                                                        0% { transform: rotate(0deg); }
                                                        100% { transform: rotate(360deg); }
                                                    }
                                                `}
                                            </style>

                                        </>
                                    ) : 'Submit'}
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* Display first user's name above the scoring table, outside the gray box */}
                {firstUser && (
                    <div style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '1.2em',
                        marginBottom: theme.spacing.SMALL,
                        color: theme.colors.TEXT
                    }}>
                        {firstUser.displayName || 'Player'}
                    </div>
                )}

                {/* Score summary row (dashboard style) */}
                {firstUser && courseModel && roundModel && (() => {
                    // Calculate gross, net, and relative to par
                    const holes = courseModel.scorecard.holes.filter(hole => {
                        if (roundModel.setup.holesToPlay === 'front9') return hole.number <= 9;
                        if (roundModel.setup.holesToPlay === 'back9') return hole.number > 9;
                        return true;
                    });
                    let gross = 0;
                    let net = 0;
                    let totalPar = 0;
                    const handicap = firstUser.handicap || 0;
                    holes.forEach(hole => {
                        const stat = firstUser.holeStats.find(h => h.hole === hole.number);
                        const score = stat?.grossScore || 0;
                        if (score > 0) {
                            gross += score;
                            totalPar += hole.par;
                            // Use calcNetScore if available, else fallback to gross
                            if (typeof calcNetScore === 'function') {
                                net += calcNetScore({
                                    holesToPlay: roundModel.setup.holesToPlay,
                                    grossScore: score,
                                    holeInfo: hole,
                                    handicap: handicap
                                }) || 0;
                            } else {
                                net += score;
                            }
                        }
                    });
                    const toPar = gross - totalPar;
                    return (
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: theme.spacing.SMALL,
                            marginBottom: theme.spacing.MEDIUM,
                            maxWidth: '400px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            padding: '0 24px',
                            alignItems: 'center',
                            borderRadius: '8px',
                            fontSize: '1.3em',
                            fontWeight: 500,
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.MEDIUM
                            }}>
                                <span style={{
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontWeight: 'bold'
                                }}>Gross:</span>
                                <span style={{
                                    color: theme.colors.TEXT,
                                    fontWeight: 'bold',
                                    fontSize: '1.2em'
                                }}>{gross}</span>
                            </div>
                            {toPar === 0 ? (
                                <span style={{
                                    color: theme.colors.PAR_TEXT,
                                    backgroundColor: theme.colors.PAR,
                                    fontWeight: 'bold',
                                    fontSize: '1.2em',
                                    width: '52px',
                                    height: '52px',
                                    margin: '0 4px',
                                    lineHeight: '24px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px',
                                }}>E</span>
                            ) : (
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: theme.colors.WHITE,
                                    backgroundColor: toPar > 0 ? theme.colors.OVER_PAR : theme.colors.UNDER_PAR,
                                    fontWeight: 'bold',
                                    fontSize: '1.3em',
                                    width: '52px',
                                    height: '52px',
                                    borderRadius: toPar < 0 ? '50%' : '4px',
                                    margin: '0 4px',
                                    lineHeight: '24px',
                                }}>{toPar > 0 ? `+${toPar}` : toPar}</span>
                            )}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.MEDIUM
                            }}>
                                <span style={{
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontWeight: 'bold'
                                }}>Net:</span>
                                <span style={{
                                    color: theme.colors.TEXT,
                                    fontWeight: 'bold',
                                    fontSize: '1.2em'
                                }}>{net}</span>
                            </div>
                        </div>
                    );
                })()}

                <div style={{
                    backgroundColor: theme.colors.BACKGROUND,
                    paddingLeft: 0,
                    paddingRight: theme.spacing.SMALL,
                    paddingBottom: theme.spacing.LARGE,
                    paddingTop: theme.spacing.MEDIUM,
                    borderRadius: theme.borderRadius.MEDIUM,
                    overflowX: 'auto',
                    marginBottom: theme.spacing.XLARGE,
                    position: 'relative'
                }} ref={scorecardRef}>
                    <div style={{
                        position: 'sticky',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: '40px',
                        background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.1))',
                        pointerEvents: 'none',
                        zIndex: 20,
                        float: 'right',
                        height: '100%'
                    }} />
                    <table style={{
                        borderCollapse: 'separate',
                        borderSpacing: '0',
                        marginLeft: 0,
                        color: theme.colors.TEXT,
                        tableLayout: 'auto',
                        border: 0
                    }}>
                        <thead>
                            <tr>
                                {/* Hole number column header */}
                                <th style={{
                                    padding: '0',
                                    textAlign: 'left',
                                    width: '70px',
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: theme.colors.BACKGROUND,
                                    zIndex: 10,
                                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                    clipPath: 'inset(0 -4px 0 0)',
                                    border: 0,
                                    margin: 0
                                }}>Hole</th>
                                {courseModel.scorecard.holes
                                    .filter((_, index) => {
                                        if (!roundModel?.setup?.holesToPlay) return true;
                                        if (roundModel.setup.holesToPlay === 'all18') return true;
                                        if (roundModel.setup.holesToPlay === 'front9') return index < 9;
                                        if (roundModel.setup.holesToPlay === 'back9') return index >= 9;
                                        return true;
                                    })
                                    .map((hole, index) => {
                                        const holeNumber = roundModel?.setup?.holesToPlay === 'back9' ? index + 10 : index + 1;
                                        const user = Object.values(roundModel.users)[0];
                                        let numDots = 0;
                                        if (user && typeof user.handicap === 'number') {
                                            numDots = calcStrokesGiven(user.handicap, hole, roundModel.setup.holesToPlay);
                                        }
                                        // Insert 'Out' column after hole 9 for 18-hole rounds
                                        if (roundModel.setup.holesToPlay === 'all18' && holeNumber === 9) {
                                            return (
                                                <React.Fragment key={`hole-${index}`}>
                                                    <th style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '40px',
                                                        lineHeight: '24px',
                                                        position: 'relative',
                                                        verticalAlign: 'bottom'
                                                    }}>
                                                        <div style={{ height: '10px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '2px' }}>
                                                            {numDots > 0 && Array.from({ length: numDots }).map((_, i) => (
                                                                <span key={i} style={{
                                                                    display: 'inline-block',
                                                                    width: '7px',
                                                                    height: '7px',
                                                                    borderRadius: '50%',
                                                                    background: theme.colors.BLACK,
                                                                    marginBottom: '2px'
                                                                }} />
                                                            ))}
                                                        </div>
                                                        {holeNumber}
                                                    </th>
                                                    {/* Out column header */}
                                                    <th style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '50px',
                                                        backgroundColor: theme.colors.SURFACE,
                                                        height: '40px',
                                                        lineHeight: '24px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        Out
                                                    </th>
                                                </React.Fragment>
                                            );
                                        }
                                        // Normal hole header with dots
                                        return (
                                            <th style={{
                                                padding: theme.spacing.SMALL,
                                                textAlign: 'center',
                                                width: '40px',
                                                minWidth: '40px',
                                                height: '40px',
                                                minHeight: '40px',
                                                lineHeight: '24px',
                                                position: 'relative',
                                                verticalAlign: 'bottom'
                                            }} key={holeNumber}>
                                                <div style={{ height: '10px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '2px' }}>
                                                    {numDots > 0 && Array.from({ length: numDots }).map((_, i) => (
                                                        <span key={i} style={{
                                                            display: 'inline-block',
                                                            width: '7px',
                                                            height: '7px',
                                                            borderRadius: '50%',
                                                            background: theme.colors.BLACK,
                                                            marginBottom: '2px'
                                                        }} />
                                                    ))}
                                                </div>
                                                {holeNumber}
                                            </th>
                                        );
                                    })}
                                {/* Back 9 total column header */}
                                {roundModel.setup.holesToPlay === 'all18' && (
                                    <>
                                        <th style={{
                                            padding: theme.spacing.SMALL,
                                            textAlign: 'center',
                                            width: '50px',
                                            height: '40px',
                                            lineHeight: '24px',
                                            backgroundColor: theme.colors.SURFACE,
                                            fontWeight: 'bold'
                                        }}>
                                            In
                                        </th>
                                        {/* Total score column header */}
                                        <th style={{
                                            padding: theme.spacing.SMALL,
                                            textAlign: 'center',
                                            width: '60px',
                                            height: '40px',
                                            lineHeight: '24px',
                                            backgroundColor: theme.colors.SURFACE,
                                            fontWeight: 'bold'
                                        }}>
                                            Total
                                        </th>
                                    </>
                                )}
                                {/* Total column header for 9-hole rounds */}
                                {(roundModel.setup.holesToPlay === 'front9' || roundModel.setup.holesToPlay === 'back9') && (
                                    <th style={{
                                        padding: theme.spacing.SMALL,
                                        textAlign: 'center',
                                        width: '60px',
                                        height: '40px',
                                        lineHeight: '24px',
                                        backgroundColor: theme.colors.SURFACE,
                                        fontWeight: 'bold'
                                    }}>
                                        Total
                                    </th>
                                )}
                            </tr>
                            <tr>
                                {/* Par row header */}
                                <th style={{
                                    padding: '4px 0',
                                    textAlign: 'left',
                                    width: '70px',
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontWeight: 'normal',
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: theme.colors.BACKGROUND,
                                    zIndex: 10,
                                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                    clipPath: 'inset(0 -4px 0 0)',
                                    border: 0,
                                    margin: 0,
                                    fontSize: '0.85em'
                                }}>Par</th>
                                {courseModel.scorecard.holes
                                    .filter((_, index) => {
                                        if (!roundModel?.setup?.holesToPlay) return true;
                                        if (roundModel.setup.holesToPlay === 'all18') return true;
                                        if (roundModel.setup.holesToPlay === 'front9') return index < 9;
                                        if (roundModel.setup.holesToPlay === 'back9') return index >= 9;
                                        return true;
                                    })
                                    .map((hole, index) => {
                                        // Insert "Out" column after hole 9 for 18-hole rounds
                                        if (roundModel.setup.holesToPlay === 'all18' && index === 8) {
                                            return (
                                                <React.Fragment key={`par-${index}`}>
                                                    <th style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '30px',
                                                        lineHeight: '18px',
                                                        color: theme.colors.TEXT_SECONDARY,
                                                        fontWeight: 'normal',
                                                        fontSize: '0.85em'
                                                    }}>
                                                        {hole.par}
                                                    </th>
                                                    <th style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '30px',
                                                        lineHeight: '18px',
                                                        color: theme.colors.TEXT_SECONDARY,
                                                        fontWeight: 'bold',
                                                        backgroundColor: theme.colors.SURFACE,
                                                    }}>
                                                        {courseModel?.scorecard?.holes
                                                            ?.filter((_, i) => i < 9)
                                                            .reduce((total, h) => total + h.par, 0)}
                                                    </th>
                                                </React.Fragment>
                                            );
                                        }
                                        if (index === 8) {
                                            return (
                                                <React.Fragment key={`par-${index}`}>
                                                    <th style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '30px',
                                                        lineHeight: '18px',
                                                        color: theme.colors.TEXT_SECONDARY,
                                                        fontWeight: 'normal',
                                                        fontSize: '0.85em'
                                                    }}>
                                                        {hole.par}
                                                    </th>
                                                    <th style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '30px',
                                                        lineHeight: '18px',
                                                        color: theme.colors.TEXT_SECONDARY,
                                                        fontWeight: 'bold',
                                                        backgroundColor: theme.colors.SURFACE,
                                                    }}>
                                                        {courseModel?.scorecard?.holes
                                                            ?.filter((_, i) => i < 9)
                                                            .reduce((total, h) => total + h.par, 0)}
                                                    </th>
                                                </React.Fragment>
                                            );
                                        }

                                        return (
                                            <th key={index} style={{
                                                padding: theme.spacing.SMALL,
                                                textAlign: 'center',
                                                width: '40px',
                                                height: '30px',
                                                lineHeight: '18px',
                                                color: theme.colors.TEXT_SECONDARY,
                                                fontWeight: 'normal',
                                                fontSize: '0.85em'
                                            }}>
                                                {hole.par}
                                            </th>
                                        );
                                    })}
                                {roundModel.setup.holesToPlay === 'all18' && (
                                    <>
                                        <th style={{
                                            padding: theme.spacing.SMALL,
                                            textAlign: 'center',
                                            width: '40px',
                                            height: '40px',
                                            lineHeight: '24px',
                                            color: theme.colors.TEXT_SECONDARY,
                                            backgroundColor: theme.colors.SURFACE,
                                            fontWeight: 'bold'
                                        }}>
                                            {courseModel?.scorecard?.holes
                                                ?.filter((_, i) => i >= 9)
                                                .reduce((total, h) => total + h.par, 0)}
                                        </th>
                                        <th style={{
                                            padding: theme.spacing.SMALL,
                                            textAlign: 'center',
                                            width: '40px',
                                            height: '40px',
                                            lineHeight: '24px',
                                            color: theme.colors.TEXT_SECONDARY,
                                            backgroundColor: theme.colors.SURFACE,
                                            fontWeight: 'bold'
                                        }}>
                                            {courseModel?.scorecard?.holes
                                                .reduce((total, h) => total + h.par, 0)}
                                        </th>
                                    </>
                                )}
                            </tr>
                            <tr>
                                {/* Index row header */}
                                <th style={{
                                    padding: '4px 0',
                                    textAlign: 'left',
                                    width: '70px',
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontWeight: 'normal',
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: theme.colors.BACKGROUND,
                                    zIndex: 10,
                                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                    clipPath: 'inset(0 -4px 0 0)',
                                    border: 0,
                                    margin: 0,
                                    fontSize: '0.85em'
                                }}>Index</th>
                                {courseModel.scorecard.holes
                                    .filter((_, index) => {
                                        if (!roundModel?.setup?.holesToPlay) return true;
                                        if (roundModel.setup.holesToPlay === 'all18') return true;
                                        if (roundModel.setup.holesToPlay === 'front9') return index < 9;
                                        if (roundModel.setup.holesToPlay === 'back9') return index >= 9;
                                        return true;
                                    })
                                    .map((hole, index) => {
                                        // Insert "Out" column after hole 9 for 18-hole rounds
                                        if (roundModel.setup.holesToPlay === 'all18' && index === 8) {
                                            return (
                                                <React.Fragment key={`index-${index}`}>
                                                    <th style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '30px',
                                                        lineHeight: '18px',
                                                        color: theme.colors.TEXT_SECONDARY,
                                                        fontWeight: 'normal',
                                                        fontSize: '0.85em'
                                                    }}>
                                                        {roundModel.setup.holesToPlay === 'all18' ? hole.mensHandicap18 : hole.mensHandicap9}
                                                    </th>
                                                    <th style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '30px',
                                                        lineHeight: '18px',
                                                        color: theme.colors.TEXT_SECONDARY,
                                                        backgroundColor: theme.colors.SURFACE,
                                                        fontWeight: 'normal',
                                                        fontSize: '0.85em'
                                                    }}>
                                                        -
                                                    </th>
                                                </React.Fragment>
                                            );
                                        }

                                        if (index === 8) {
                                            return (
                                                <React.Fragment key={`index-${index}`}>
                                                    <th style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '30px',
                                                        lineHeight: '18px',
                                                        color: theme.colors.TEXT_SECONDARY,
                                                        fontWeight: 'normal',
                                                        fontSize: '0.85em'
                                                    }}>
                                                        {roundModel.setup.holesToPlay === 'all18' ? hole.mensHandicap18 : hole.mensHandicap9}
                                                    </th>
                                                    <th style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '30px',
                                                        lineHeight: '18px',
                                                        color: theme.colors.TEXT_SECONDARY,
                                                        backgroundColor: theme.colors.SURFACE,
                                                        fontWeight: 'normal',
                                                        fontSize: '0.85em'
                                                    }}>
                                                        -
                                                    </th>
                                                </React.Fragment>
                                            );
                                        }

                                        return (
                                            <th key={index} style={{
                                                padding: theme.spacing.SMALL,
                                                textAlign: 'center',
                                                width: '40px',
                                                height: '30px',
                                                lineHeight: '18px',
                                                color: theme.colors.TEXT_SECONDARY,
                                                fontWeight: 'normal',
                                                fontSize: '0.85em'
                                            }}>
                                                {roundModel.setup.holesToPlay === 'all18' ? hole.mensHandicap18 : hole.mensHandicap9}
                                            </th>
                                        );
                                    })}
                                {/* Add Out column for 18-hole rounds between holes 9 and 10 */}
                                {roundModel.setup.holesToPlay === 'all18' && (
                                    <>
                                        <th style={{
                                            padding: theme.spacing.SMALL,
                                            textAlign: 'center',
                                            width: '40px',
                                            height: '40px',
                                            lineHeight: '24px',
                                            color: theme.colors.TEXT_SECONDARY,
                                            backgroundColor: theme.colors.SURFACE,
                                            fontWeight: 'normal'
                                        }}>
                                            -
                                        </th>
                                        <th style={{
                                            padding: theme.spacing.SMALL,
                                            textAlign: 'center',
                                            width: '40px',
                                            height: '40px',
                                            lineHeight: '24px',
                                            color: theme.colors.TEXT_SECONDARY,
                                            backgroundColor: theme.colors.SURFACE,
                                            fontWeight: 'normal'
                                        }}>
                                            -
                                        </th>
                                    </>
                                )}
                            </tr>
                            <tr>
                                {/* Score row header */}
                                <th style={{
                                    padding: '8px 0',
                                    textAlign: 'left',
                                    width: '70px',
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontWeight: 'normal',
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: theme.colors.BACKGROUND,
                                    zIndex: 10,
                                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                    clipPath: 'inset(0 -4px 0 0)',
                                    border: 0,
                                    margin: 0
                                }}>Score</th>
                                {Object.values(roundModel.users).map((player) => (
                                    <React.Fragment key={player.uid}>
                                        {courseModel.scorecard.holes
                                            .filter((_, index) => {
                                                if (roundModel.setup.holesToPlay === 'all18') return true;
                                                if (roundModel.setup.holesToPlay === 'front9') return index < 9;
                                                if (roundModel.setup.holesToPlay === 'back9') return index >= 9;
                                                return true;
                                            })
                                            .map((hole, index) => {
                                                const holeNumber = roundModel.setup.holesToPlay === 'back9' ? index + 10 : index + 1;
                                                const holeStat = player.holeStats.find(stat => stat.hole === holeNumber);

                                                // Insert "Out" column after hole 9 for 18-hole rounds
                                                if (roundModel.setup.holesToPlay === 'all18' && holeNumber === 9) {
                                                    return (
                                                        <React.Fragment key={`${player.uid}-score-${holeNumber}`}>
                                                            <th
                                                                onClick={() => handleScoreClick(holeNumber)}
                                                                style={{
                                                                    padding: '8px',
                                                                    textAlign: 'center',
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    lineHeight: '24px',
                                                                    backgroundColor: holeStat && holeStat.grossScore !== null ?
                                                                        getScoreColors(theme, holeStat.grossScore, hole.par).bg :
                                                                        'transparent',
                                                                    color: holeStat && holeStat.grossScore !== null ?
                                                                        getScoreColors(theme, holeStat.grossScore, hole.par).text :
                                                                        theme.colors.TEXT,
                                                                    fontWeight: 'bold',
                                                                    borderRadius: holeStat && holeStat.grossScore !== null && holeStat.grossScore < hole.par ? '50%' :
                                                                        holeStat && holeStat.grossScore !== null && holeStat.grossScore > hole.par ? '4px' : '0',
                                                                    cursor: canUserInteract() ? 'pointer' : 'default'
                                                                }}
                                                            >
                                                                {holeStat?.grossScore || ''}
                                                            </th>
                                                            <th style={{
                                                                padding: '8px',
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                fontWeight: 'bold',
                                                                backgroundColor: theme.colors.SURFACE
                                                            }}>
                                                                {player.holeStats
                                                                    .filter(stat => stat.hole <= 9)
                                                                    .reduce((total, stat) => total + (stat.grossScore || 0), 0) || ''}
                                                            </th>
                                                        </React.Fragment>
                                                    );
                                                }


                                                return (
                                                    <th key={`${player.uid}-score-${holeNumber}`}
                                                        onClick={() => handleScoreClick(holeNumber)}
                                                        style={{
                                                            padding: '8px',
                                                            textAlign: 'center',
                                                            width: '40px',
                                                            height: '40px',
                                                            lineHeight: '24px',
                                                            backgroundColor: holeStat && holeStat.grossScore !== null ?
                                                                getScoreColors(theme, holeStat.grossScore, hole.par).bg :
                                                                'transparent',
                                                            color: holeStat && holeStat.grossScore !== null ?
                                                                getScoreColors(theme, holeStat.grossScore, hole.par).text :
                                                                theme.colors.TEXT,
                                                            fontWeight: 'bold',
                                                            borderRadius: holeStat && holeStat.grossScore !== null && holeStat.grossScore < hole.par ? '50%' :
                                                                holeStat && holeStat.grossScore !== null && holeStat.grossScore > hole.par ? '4px' : '0',
                                                            cursor: canUserInteract() ? 'pointer' : 'default'
                                                        }}
                                                    >
                                                        {holeStat?.grossScore || ''}
                                                    </th>
                                                );
                                            })}
                                        {/* Add In and Total columns for 18-hole rounds */}
                                        {roundModel.setup.holesToPlay === 'all18' && (
                                            <>
                                                <th style={{
                                                    padding: '8px',
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    fontWeight: 'bold',
                                                    backgroundColor: theme.colors.SURFACE
                                                }}>
                                                    {player.holeStats
                                                        .filter(stat => stat.hole > 9)
                                                        .reduce((total, stat) => total + (stat.grossScore || 0), 0) || ''}
                                                </th>
                                                <th style={{
                                                    padding: '8px',
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    fontWeight: 'bold',
                                                    backgroundColor: theme.colors.SURFACE
                                                }}>
                                                    {player.holeStats
                                                        .reduce((total, stat) => total + (stat.grossScore || 0), 0) || ''}
                                                </th>
                                            </>
                                        )}
                                        {(roundModel.setup.holesToPlay === 'front9' || roundModel.setup.holesToPlay === 'back9') && (
                                            <th style={{
                                                padding: '8px',
                                                textAlign: 'center',
                                                width: '40px',
                                                height: '40px',
                                                lineHeight: '24px',
                                                fontWeight: 'bold',
                                                backgroundColor: theme.colors.SURFACE
                                            }}>
                                                {player.holeStats
                                                    .filter(stat => {
                                                        if (roundModel.setup.holesToPlay === 'front9') return stat.hole <= 9;
                                                        if (roundModel.setup.holesToPlay === 'back9') return stat.hole > 9;
                                                        return false;
                                                    })
                                                    .reduce((total, stat) => total + (stat.grossScore || 0), 0) || ''}
                                            </th>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tr>
                            {statsExpanderOpen &&
                                <>
                                    <tr>
                                        {/* Fairway row header */}
                                        <th style={{
                                            padding: '8px 0',
                                            textAlign: 'left',
                                            width: '70px',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontWeight: 'normal',
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: theme.colors.BACKGROUND,
                                            zIndex: 10,
                                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                            clipPath: 'inset(0 -4px 0 0)',
                                            border: 0,
                                            margin: 0
                                        }}>Fairway</th>
                                        {courseModel.scorecard.holes
                                            .filter((_, index) => {
                                                if (!roundModel?.setup?.holesToPlay) return true;
                                                if (roundModel.setup.holesToPlay === 'all18') return true;
                                                if (roundModel.setup.holesToPlay === 'front9') return index < 9;
                                                if (roundModel.setup.holesToPlay === 'back9') return index >= 9;
                                                return true;
                                            })
                                            .map((_, index) => {
                                                const holeNumber = roundModel.setup.holesToPlay === 'back9' ? index + 10 : index + 1;
                                                const player = Object.values(roundModel.users)[0]; // Get the first player
                                                const holeStat = player.holeStats.find(stat => stat.hole === holeNumber);
                                                const isFairwayHit = holeStat?.fairwayHit === true ? true : holeStat?.fairwayHit === false ? false : null;
                                                const hasFairwayData = holeStat?.fairwayHit === true || holeStat?.fairwayHit == false ? true : false;

                                                // Insert "Out" column after hole 9 for 18-hole rounds
                                                if (roundModel.setup.holesToPlay === 'all18' && holeNumber === 9) {
                                                    return (
                                                        <React.Fragment key={`fairway-${index}`}>
                                                            <th style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                fontWeight: 'bold',
                                                                color: hasFairwayData ? (isFairwayHit ? theme.colors.PAR_TEXT : theme.colors.UNDER_PAR_TEXT) : theme.colors.TEXT_SECONDARY,
                                                                borderRadius: hasFairwayData ? '4px' : '0',
                                                                cursor: canUserInteract() ? 'pointer' : 'default'
                                                            }}
                                                                onClick={() => handleScoreClick(holeNumber)}
                                                            >
                                                                {hasFairwayData ? (isFairwayHit ? '✓' : '✗') : '-'}
                                                            </th>
                                                            <th style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                color: theme.colors.TEXT_SECONDARY,
                                                                backgroundColor: theme.colors.SURFACE,
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {Object.values(roundModel.users)[0]?.holeStats
                                                                    .filter(stat => stat.hole <= 9)
                                                                    .filter(stat => stat.fairwayHit === true).length || '0'}
                                                            </th>
                                                        </React.Fragment>
                                                    );
                                                }

                                                return (
                                                    <th key={index} style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '40px',
                                                        lineHeight: '24px',
                                                        fontWeight: 'normal',
                                                        color: hasFairwayData ? (isFairwayHit ? theme.colors.PAR_TEXT : theme.colors.UNDER_PAR_TEXT) : theme.colors.TEXT_SECONDARY,
                                                        borderRadius: hasFairwayData ? '4px' : '0',
                                                        cursor: canUserInteract() ? 'pointer' : 'default'
                                                    }}
                                                        onClick={() => handleScoreClick(holeNumber)}
                                                    >
                                                        {hasFairwayData ? (isFairwayHit ? '✓' : '✗') : '-'}
                                                    </th>
                                                );
                                            })}
                                        {/* Add Out column for 18-hole rounds between holes 9 and 10 */}
                                        {roundModel.setup.holesToPlay === 'all18' && (
                                            <>
                                                <th style={{
                                                    padding: theme.spacing.SMALL,
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    color: theme.colors.TEXT_SECONDARY,
                                                    backgroundColor: theme.colors.SURFACE,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {Object.values(roundModel.users)[0]?.holeStats
                                                        .filter(stat => stat.hole > 9)
                                                        .filter(stat => stat.fairwayHit === true).length || '0'}
                                                </th>
                                                <th style={{
                                                    padding: theme.spacing.SMALL,
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    color: theme.colors.TEXT_SECONDARY,
                                                    backgroundColor: theme.colors.SURFACE,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {Object.values(roundModel.users)[0]?.holeStats
                                                        .filter(stat => stat.fairwayHit === true).length || '0'}
                                                </th>
                                            </>
                                        )}
                                        {/* Add total column for 9-hole rounds */}
                                        {(roundModel.setup.holesToPlay === 'front9' || roundModel.setup.holesToPlay === 'back9') && (
                                            <th style={{
                                                padding: theme.spacing.SMALL,
                                                textAlign: 'center',
                                                width: '40px',
                                                height: '40px',
                                                lineHeight: '24px',
                                                color: theme.colors.TEXT_SECONDARY,
                                                backgroundColor: theme.colors.SURFACE,
                                                fontWeight: 'bold'
                                            }}>
                                                {Object.values(roundModel.users)[0]?.holeStats
                                                    .filter(stat => {
                                                        if (roundModel.setup.holesToPlay === 'front9') return stat.hole <= 9;
                                                        if (roundModel.setup.holesToPlay === 'back9') return stat.hole > 9;
                                                        return false;
                                                    })
                                                    .filter(stat => stat.fairwayHit === true).length || '0'}
                                            </th>
                                        )}
                                    </tr>
                                    <tr>
                                        {/* GIR row header */}
                                        <th style={{
                                            padding: '8px 0',
                                            textAlign: 'left',
                                            width: '70px',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontWeight: 'normal',
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: theme.colors.BACKGROUND,
                                            zIndex: 10,
                                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                            clipPath: 'inset(0 -4px 0 0)',
                                            border: 0,
                                            margin: 0
                                        }}>GIR</th>
                                        {courseModel.scorecard.holes
                                            .filter((_, index) => {
                                                if (!roundModel?.setup?.holesToPlay) return true;
                                                if (roundModel.setup.holesToPlay === 'all18') return true;
                                                if (roundModel.setup.holesToPlay === 'front9') return index < 9;
                                                if (roundModel.setup.holesToPlay === 'back9') return index >= 9;
                                                return true;
                                            })
                                            .map((_, index) => {
                                                const holeNumber = roundModel.setup.holesToPlay === 'back9' ? index + 10 : index + 1;
                                                const player = Object.values(roundModel.users)[0]; // Get the first player
                                                const holeStat = player.holeStats.find(stat => stat.hole === holeNumber);
                                                const isGIR = holeStat?.greenInRegulation === true ? true : holeStat?.greenInRegulation === false ? false : null;
                                                const hasGIRData = holeStat?.greenInRegulation === true || holeStat?.greenInRegulation == false ? true : false;

                                                // Insert "Out" column after hole 9 for 18-hole rounds
                                                if (roundModel.setup.holesToPlay === 'all18' && holeNumber === 9) {
                                                    return (
                                                        <React.Fragment key={`gir-${index}`}>
                                                            <th style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                fontWeight: 'bold',
                                                                color: hasGIRData ? (isGIR ? theme.colors.PAR_TEXT : theme.colors.UNDER_PAR_TEXT) : theme.colors.TEXT_SECONDARY,
                                                                borderRadius: hasGIRData ? '4px' : '0',
                                                                cursor: canUserInteract() ? 'pointer' : 'default'
                                                            }}
                                                                onClick={() => handleScoreClick(holeNumber)}
                                                            >
                                                                {hasGIRData ? (isGIR ? '✓' : '✗') : '-'}
                                                            </th>
                                                            <th style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                color: theme.colors.TEXT_SECONDARY,
                                                                backgroundColor: theme.colors.SURFACE,
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {Object.values(roundModel.users)[0]?.holeStats
                                                                    .filter(stat => stat.hole <= 9)
                                                                    .filter(stat => stat.greenInRegulation === true).length || '0'}
                                                            </th>
                                                        </React.Fragment>
                                                    );
                                                }

                                                return (
                                                    <th key={index} style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '40px',
                                                        lineHeight: '24px',
                                                        fontWeight: 'normal',
                                                        color: hasGIRData ? (isGIR ? theme.colors.PAR_TEXT : theme.colors.UNDER_PAR_TEXT) : theme.colors.TEXT_SECONDARY,
                                                        borderRadius: hasGIRData ? '4px' : '0',
                                                        cursor: canUserInteract() ? 'pointer' : 'default'
                                                    }}
                                                        onClick={() => handleScoreClick(holeNumber)}
                                                    >
                                                        {hasGIRData ? (isGIR ? '✓' : '✗') : '-'}
                                                    </th>
                                                );
                                            })}
                                        {/* Add Out column for 18-hole rounds between holes 9 and 10 */}
                                        {roundModel.setup.holesToPlay === 'all18' && (
                                            <>
                                                <th style={{
                                                    padding: theme.spacing.SMALL,
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    color: theme.colors.TEXT_SECONDARY,
                                                    backgroundColor: theme.colors.SURFACE,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {Object.values(roundModel.users)[0]?.holeStats
                                                        .filter(stat => stat.hole > 9)
                                                        .filter(stat => stat.greenInRegulation === true).length || '0'}
                                                </th>
                                                <th style={{
                                                    padding: theme.spacing.SMALL,
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    color: theme.colors.TEXT_SECONDARY,
                                                    backgroundColor: theme.colors.SURFACE,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {Object.values(roundModel.users)[0]?.holeStats
                                                        .filter(stat => stat.greenInRegulation === true).length || '0'}
                                                </th>
                                            </>
                                        )}
                                        {/* Add total column for 9-hole rounds */}
                                        {(roundModel.setup.holesToPlay === 'front9' || roundModel.setup.holesToPlay === 'back9') && (
                                            <th style={{
                                                padding: theme.spacing.SMALL,
                                                textAlign: 'center',
                                                width: '40px',
                                                height: '40px',
                                                lineHeight: '24px',
                                                color: theme.colors.TEXT_SECONDARY,
                                                backgroundColor: theme.colors.SURFACE,
                                                fontWeight: 'bold'
                                            }}>
                                                {Object.values(roundModel.users)[0]?.holeStats
                                                    .filter(stat => {
                                                        if (roundModel.setup.holesToPlay === 'front9') return stat.hole <= 9;
                                                        if (roundModel.setup.holesToPlay === 'back9') return stat.hole > 9;
                                                        return false;
                                                    })
                                                    .filter(stat => stat.greenInRegulation === true).length || '0'}
                                            </th>
                                        )}
                                    </tr>
                                    <tr>
                                        {/* Chips row header */}
                                        <th style={{
                                            padding: '8px 0',
                                            textAlign: 'left',
                                            width: '70px',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontWeight: 'normal',
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: theme.colors.BACKGROUND,
                                            zIndex: 10,
                                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                            clipPath: 'inset(0 -4px 0 0)',
                                            border: 0,
                                            margin: 0
                                        }}>Chips</th>
                                        {courseModel.scorecard.holes
                                            .filter((_, index) => {
                                                if (!roundModel?.setup?.holesToPlay) return true;
                                                if (roundModel.setup.holesToPlay === 'all18') return true;
                                                if (roundModel.setup.holesToPlay === 'front9') return index < 9;
                                                if (roundModel.setup.holesToPlay === 'back9') return index >= 9;
                                                return true;
                                            })
                                            .map((_, index) => {
                                                const holeNumber = roundModel.setup.holesToPlay === 'back9' ? index + 10 : index + 1;
                                                const player = Object.values(roundModel.users)[0]; // Get the first player
                                                const holeStat = player.holeStats.find(stat => stat.hole === holeNumber);
                                                const hasChipsData = holeStat && holeStat.chips !== null;

                                                // Insert "Out" column after hole 9 for 18-hole rounds
                                                if (roundModel.setup.holesToPlay === 'all18' && holeNumber === 9) {
                                                    return (
                                                        <React.Fragment key={`chips-${index}`}>
                                                            <th style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                fontWeight: 'normal',
                                                                color: hasChipsData ? theme.colors.TEXT : theme.colors.TEXT_SECONDARY,
                                                                borderRadius: hasChipsData ? '4px' : '0',
                                                                cursor: canUserInteract() ? 'pointer' : 'default'
                                                            }}
                                                                onClick={() => handleScoreClick(holeNumber)}
                                                            >
                                                                {hasChipsData ? holeStat.chips : '-'}
                                                            </th>
                                                            <th style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                color: theme.colors.TEXT_SECONDARY,
                                                                backgroundColor: theme.colors.SURFACE,
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {Object.values(roundModel.users)[0]?.holeStats
                                                                    .filter(stat => stat.hole <= 9)
                                                                    .reduce((total, stat) => total + (stat.chips || 0), 0) || '0'}
                                                            </th>
                                                        </React.Fragment>
                                                    );
                                                }

                                                return (
                                                    <th key={index} style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '40px',
                                                        lineHeight: '24px',
                                                        fontWeight: 'normal',
                                                        color: hasChipsData ? theme.colors.TEXT : theme.colors.TEXT_SECONDARY,
                                                        borderRadius: hasChipsData ? '4px' : '0',
                                                        cursor: canUserInteract() ? 'pointer' : 'default'
                                                    }}
                                                        onClick={() => handleScoreClick(holeNumber)}
                                                    >
                                                        {hasChipsData ? holeStat.chips : '-'}
                                                    </th>
                                                );
                                            })}
                                        {/* Add Out column for 18-hole rounds between holes 9 and 10 */}
                                        {roundModel.setup.holesToPlay === 'all18' && (
                                            <>
                                                <th style={{
                                                    padding: theme.spacing.SMALL,
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    color: theme.colors.TEXT_SECONDARY,
                                                    backgroundColor: theme.colors.SURFACE,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {Object.values(roundModel.users)[0]?.holeStats
                                                        .filter(stat => stat.hole > 9)
                                                        .reduce((total, stat) => total + (stat.chips || 0), 0) || '0'}
                                                </th>
                                                <th style={{
                                                    padding: theme.spacing.SMALL,
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    color: theme.colors.TEXT_SECONDARY,
                                                    backgroundColor: theme.colors.SURFACE,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {Object.values(roundModel.users)[0]?.holeStats
                                                        .reduce((total, stat) => total + (stat.chips || 0), 0) || '0'}
                                                </th>
                                            </>
                                        )}
                                        {/* Add total column for 9-hole rounds */}
                                        {(roundModel.setup.holesToPlay === 'front9' || roundModel.setup.holesToPlay === 'back9') && (
                                            <th style={{
                                                padding: theme.spacing.SMALL,
                                                textAlign: 'center',
                                                width: '40px',
                                                height: '40px',
                                                lineHeight: '24px',
                                                color: theme.colors.TEXT_SECONDARY,
                                                backgroundColor: theme.colors.SURFACE,
                                                fontWeight: 'bold'
                                            }}>
                                                {Object.values(roundModel.users)[0]?.holeStats
                                                    .filter(stat => {
                                                        if (roundModel.setup.holesToPlay === 'front9') return stat.hole <= 9;
                                                        if (roundModel.setup.holesToPlay === 'back9') return stat.hole > 9;
                                                        return false;
                                                    })
                                                    .reduce((total, stat) => total + (stat.chips || 0), 0) || '0'}
                                            </th>
                                        )}
                                    </tr>
                                    <tr>
                                        {/* Putts row header */}
                                        <th style={{
                                            padding: '8px 0',
                                            textAlign: 'left',
                                            width: '70px',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontWeight: 'normal',
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: theme.colors.BACKGROUND,
                                            zIndex: 10,
                                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                            clipPath: 'inset(0 -4px 0 0)',
                                            border: 0,
                                            margin: 0
                                        }}>Putts</th>
                                        {courseModel.scorecard.holes
                                            .filter((_, index) => {
                                                if (!roundModel?.setup?.holesToPlay) return true;
                                                if (roundModel.setup.holesToPlay === 'all18') return true;
                                                if (roundModel.setup.holesToPlay === 'front9') return index < 9;
                                                if (roundModel.setup.holesToPlay === 'back9') return index >= 9;
                                                return true;
                                            })
                                            .map((_, index) => {
                                                const holeNumber = roundModel.setup.holesToPlay === 'back9' ? index + 10 : index + 1;
                                                const player = Object.values(roundModel.users)[0]; // Get the first player
                                                const holeStat = player.holeStats.find(stat => stat.hole === holeNumber);
                                                const hasPuttsData = holeStat && holeStat.putts !== null;
                                                const puttsValue = holeStat && holeStat.putts !== null ? holeStat.putts : 0;

                                                // Insert "Out" column after hole 9 for 18-hole rounds
                                                if (roundModel.setup.holesToPlay === 'all18' && holeNumber === 9) {
                                                    return (
                                                        <React.Fragment key={`putts-${index}`}>
                                                            <td style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                fontWeight: 'normal',
                                                                color: hasPuttsData ? theme.colors.TEXT : theme.colors.TEXT_SECONDARY,
                                                                borderRadius: hasPuttsData ? '4px' : '0',
                                                                cursor: canUserInteract() ? 'pointer' : 'default'
                                                            }}
                                                                onClick={() => handleScoreClick(holeNumber)}
                                                            >
                                                                {hasPuttsData ? puttsValue : '-'}
                                                            </td>
                                                            <th style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                color: theme.colors.TEXT_SECONDARY,
                                                                backgroundColor: theme.colors.SURFACE,
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {Object.values(roundModel.users)[0]?.holeStats
                                                                    .filter(stat => stat.hole <= 9)
                                                                    .reduce((total, stat) => total + (stat.putts || 0), 0) || '0'}
                                                            </th>
                                                        </React.Fragment>
                                                    );
                                                }

                                                if (roundModel.setup.holesToPlay === 'all18' && holeNumber === 9) {
                                                    return (
                                                        <React.Fragment key={`putts-${index}`}>
                                                            <td key={index} style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                fontWeight: 'normal',
                                                                color: hasPuttsData ? theme.colors.TEXT : theme.colors.TEXT_SECONDARY,
                                                                borderRadius: hasPuttsData ? '4px' : '0',
                                                                cursor: canUserInteract() ? 'pointer' : 'default'
                                                            }}
                                                                onClick={() => handleScoreClick(holeNumber)}
                                                            >
                                                                {hasPuttsData ? puttsValue : '-'}
                                                            </td>
                                                            <th style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                color: theme.colors.TEXT_SECONDARY,
                                                                backgroundColor: theme.colors.SURFACE,
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {Object.values(roundModel.users)[0]?.holeStats
                                                                    .filter(stat => stat.hole <= 9)
                                                                    .reduce((total, stat) => total + (stat.putts || 0), 0) || '0'}
                                                            </th>
                                                        </React.Fragment>
                                                    )
                                                }

                                                return (
                                                    <td key={index} style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '40px',
                                                        lineHeight: '24px',
                                                        fontWeight: 'normal',
                                                        color: hasPuttsData ? theme.colors.TEXT : theme.colors.TEXT_SECONDARY,
                                                        borderRadius: hasPuttsData ? '4px' : '0',
                                                        cursor: canUserInteract() ? 'pointer' : 'default'
                                                    }}
                                                        onClick={() => handleScoreClick(holeNumber)}
                                                    >
                                                        {hasPuttsData ? puttsValue : '-'}
                                                    </td>
                                                );
                                            })}
                                        {/* Add Out column for 18-hole rounds between holes 9 and 10 */}
                                        {roundModel.setup.holesToPlay === 'all18' && (
                                            <>
                                                <th style={{
                                                    padding: theme.spacing.SMALL,
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    color: theme.colors.TEXT_SECONDARY,
                                                    backgroundColor: theme.colors.SURFACE,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {Object.values(roundModel.users)[0]?.holeStats
                                                        .filter(stat => stat.hole > 9)
                                                        .reduce((total, stat) => total + (stat.putts || 0), 0) || '0'}
                                                </th>
                                                <th style={{
                                                    padding: theme.spacing.SMALL,
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    color: theme.colors.TEXT_SECONDARY,
                                                    backgroundColor: theme.colors.SURFACE,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {Object.values(roundModel.users)[0]?.holeStats
                                                        .reduce((total, stat) => total + (stat.putts || 0), 0) || '0'}
                                                </th>
                                            </>
                                        )}
                                        {/* Add total column for 9-hole rounds */}
                                        {(roundModel.setup.holesToPlay === 'front9' || roundModel.setup.holesToPlay === 'back9') && (
                                            <th style={{
                                                padding: theme.spacing.SMALL,
                                                textAlign: 'center',
                                                width: '40px',
                                                height: '40px',
                                                lineHeight: '24px',
                                                color: theme.colors.TEXT_SECONDARY,
                                                backgroundColor: theme.colors.SURFACE,
                                                fontWeight: 'bold'
                                            }}>
                                                {Object.values(roundModel.users)[0]?.holeStats
                                                    .filter(stat => {
                                                        if (roundModel.setup.holesToPlay === 'front9') return stat.hole <= 9;
                                                        if (roundModel.setup.holesToPlay === 'back9') return stat.hole > 9;
                                                        return false;
                                                    })
                                                    .reduce((total, stat) => total + (stat.putts || 0), 0) || '0'}
                                            </th>
                                        )}
                                    </tr>
                                    <tr>
                                        {/* Penalties row header */}
                                        <th style={{
                                            padding: '8px 0',
                                            textAlign: 'left',
                                            width: '70px',
                                            color: theme.colors.TEXT_SECONDARY,
                                            fontWeight: 'normal',
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: theme.colors.BACKGROUND,
                                            zIndex: 10,
                                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                                            clipPath: 'inset(0 -4px 0 0)',
                                            border: 0,
                                            margin: 0
                                        }}>Penalties</th>
                                        {courseModel.scorecard.holes
                                            .filter((_, index) => {
                                                if (!roundModel?.setup?.holesToPlay) return true;
                                                if (roundModel.setup.holesToPlay === 'all18') return true;
                                                if (roundModel.setup.holesToPlay === 'front9') return index < 9;
                                                if (roundModel.setup.holesToPlay === 'back9') return index >= 9;
                                                return true;
                                            })
                                            .map((_, index) => {
                                                const holeNumber = roundModel.setup.holesToPlay === 'back9' ? index + 10 : index + 1;
                                                const player = Object.values(roundModel.users)[0]; // Get the first player
                                                const holeStat = player.holeStats.find(stat => stat.hole === holeNumber);
                                                const hasPenaltiesData = holeStat && holeStat.penalties !== null && holeStat.penalties !== undefined;
                                                const penaltiesValue = holeStat && holeStat.penalties !== null ? holeStat.penalties : 0;

                                                // Insert "Out" column after hole 9 for 18-hole rounds
                                                if (roundModel.setup.holesToPlay === 'all18' && holeNumber === 9) {
                                                    return (
                                                        <React.Fragment key={`penalties-${index}`}>
                                                            <td style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                fontWeight: 'normal',
                                                                color: hasPenaltiesData ? theme.colors.TEXT : theme.colors.TEXT_SECONDARY,
                                                                borderRadius: hasPenaltiesData ? '4px' : '0',
                                                                cursor: canUserInteract() ? 'pointer' : 'default'
                                                            }}
                                                                onClick={() => handleScoreClick(holeNumber)}
                                                            >
                                                                {hasPenaltiesData ? penaltiesValue : '-'}
                                                            </td>
                                                            <th style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                color: theme.colors.TEXT_SECONDARY,
                                                                backgroundColor: theme.colors.SURFACE,
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {Object.values(roundModel.users)[0]?.holeStats
                                                                    .filter(stat => stat.hole <= 9)
                                                                    .reduce((total, stat) => total + (stat.penalties || 0), 0) || '0'}
                                                            </th>
                                                        </React.Fragment>
                                                    );
                                                }

                                                if (roundModel.setup.holesToPlay === 'all18' && holeNumber === 9) {
                                                    return (
                                                        <React.Fragment key={`penalties-${index}`}>
                                                            <td key={index} style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                fontWeight: 'normal',
                                                                color: hasPenaltiesData ? theme.colors.TEXT : theme.colors.TEXT_SECONDARY,
                                                                borderRadius: hasPenaltiesData ? '4px' : '0',
                                                                cursor: canUserInteract() ? 'pointer' : 'default'
                                                            }}
                                                                onClick={() => handleScoreClick(holeNumber)}
                                                            >
                                                                {hasPenaltiesData ? penaltiesValue : '-'}
                                                            </td><th style={{
                                                                padding: theme.spacing.SMALL,
                                                                textAlign: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                lineHeight: '24px',
                                                                color: theme.colors.TEXT_SECONDARY,
                                                                backgroundColor: theme.colors.SURFACE,
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {Object.values(roundModel.users)[0]?.holeStats
                                                                    .filter(stat => stat.hole <= 9)
                                                                    .reduce((total, stat) => total + (stat.penalties || 0), 0) || '0'}
                                                            </th>
                                                        </React.Fragment>
                                                    )
                                                }

                                                return (
                                                    <td key={index} style={{
                                                        padding: theme.spacing.SMALL,
                                                        textAlign: 'center',
                                                        width: '40px',
                                                        height: '40px',
                                                        lineHeight: '24px',
                                                        fontWeight: 'normal',
                                                        color: hasPenaltiesData ? theme.colors.TEXT : theme.colors.TEXT_SECONDARY,
                                                        borderRadius: hasPenaltiesData ? '4px' : '0',
                                                        cursor: canUserInteract() ? 'pointer' : 'default'
                                                    }}
                                                        onClick={() => handleScoreClick(holeNumber)}
                                                    >
                                                        {hasPenaltiesData ? penaltiesValue : '-'}
                                                    </td>
                                                );
                                            })}
                                        {/* Add Out column for 18-hole rounds between holes 9 and 10 */}
                                        {roundModel.setup.holesToPlay === 'all18' && (
                                            <>
                                                <th style={{
                                                    padding: theme.spacing.SMALL,
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    color: theme.colors.TEXT_SECONDARY,
                                                    backgroundColor: theme.colors.SURFACE,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {Object.values(roundModel.users)[0]?.holeStats
                                                        .filter(stat => stat.hole > 9)
                                                        .reduce((total, stat) => total + (stat.penalties || 0), 0) || '0'}
                                                </th>
                                                <th style={{
                                                    padding: theme.spacing.SMALL,
                                                    textAlign: 'center',
                                                    width: '40px',
                                                    height: '40px',
                                                    lineHeight: '24px',
                                                    color: theme.colors.TEXT_SECONDARY,
                                                    backgroundColor: theme.colors.SURFACE,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {Object.values(roundModel.users)[0]?.holeStats
                                                        .reduce((total, stat) => total + (stat.penalties || 0), 0) || '0'}
                                                </th>
                                            </>
                                        )}
                                        {/* Add total column for 9-hole rounds */}
                                        {(roundModel.setup.holesToPlay === 'front9' || roundModel.setup.holesToPlay === 'back9') && (
                                            <th style={{
                                                padding: theme.spacing.SMALL,
                                                textAlign: 'center',
                                                width: '40px',
                                                height: '40px',
                                                lineHeight: '24px',
                                                color: theme.colors.TEXT_SECONDARY,
                                                backgroundColor: theme.colors.SURFACE,
                                                fontWeight: 'bold'
                                            }}>
                                                {Object.values(roundModel.users)[0]?.holeStats
                                                    .filter(stat => {
                                                        if (roundModel.setup.holesToPlay === 'front9') return stat.hole <= 9;
                                                        if (roundModel.setup.holesToPlay === 'back9') return stat.hole > 9;
                                                        return false;
                                                    })
                                                    .filter(stat => stat.penalties !== null && stat.penalties !== undefined).length || '0'}
                                            </th>
                                        )}
                                    </tr>
                                </>}
                        </thead>
                    </table>
                </div>

                {showTutorial && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1001,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: theme.spacing.MEDIUM,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            padding: theme.spacing.LARGE,
                            borderRadius: theme.borderRadius.MEDIUM,
                            pointerEvents: 'none'
                        }}>
                            <div style={{
                                fontSize: '4em',
                                color: theme.colors.WHITE,
                                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                animation: 'slideRight 2s infinite'
                            }}>
                                👆
                            </div>
                            <div style={{
                                color: theme.colors.WHITE,
                                textAlign: 'center',
                                fontSize: '1.2em',
                                fontWeight: 'bold',
                                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                            }}>
                                Swipe to see more holes
                            </div>
                            <div style={{
                                color: theme.colors.WHITE,
                                textAlign: 'center',
                                fontSize: '1em',
                                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                            }}>
                                Tutorial will dismiss in {countdown} seconds
                            </div>
                        </div>
                    </div>
                )}

                <style>
                    {`
                        @keyframes slideRight {
                            0% { transform: translateX(-20px); }
                            50% { transform: translateX(20px); }
                            100% { transform: translateX(-20px); }
                        }
                    `}
                </style>

                <Modal
                    isOpen={modalConfig.isOpen}
                    title={modalConfig.title}
                    message={modalConfig.message}
                    type={modalConfig.type}
                    onConfirm={modalConfig.onConfirm}
                    onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                    confirmText={modalConfig.confirmText}
                    disabled={modalConfig.isLoading}
                />

                {/* Back FAB */}
                <button
                    onClick={() => navigate(-1)}
                    style={{
                        position: 'fixed',
                        bottom: theme.spacing.LARGE,
                        left: theme.spacing.LARGE,
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        backgroundColor: theme.colors.BUTTON_SECONDARY_BG,
                        color: theme.colors.BUTTON_SECONDARY_TEXT,
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        zIndex: 1000,
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                    }}
                    title="Go Back"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
                    </svg>
                </button>
            </div>
            {/* Started/Completed at info at the bottom */}
            <div style={{
                marginTop: theme.spacing.LARGE,
                marginBottom: theme.spacing.LARGE,
                display: 'flex',
                justifyContent: 'center',
                color: theme.colors.TEXT_SECONDARY,
                fontSize: '1em',
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: theme.spacing.SMALL, minWidth: 120 }}>
                    <div style={{ fontWeight: 500 }}>Started at</div>
                    <div style={{ fontWeight: 500 }}>Completed at</div>
                    <div style={{ fontWeight: 500 }}>Duration</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: theme.spacing.SMALL, minWidth: 180, marginLeft: theme.spacing.LARGE }}>
                    <div>
                        {roundModel.createdAt
                            ? (typeof roundModel.createdAt.toDate === 'function'
                                ? roundModel.createdAt.toDate().toLocaleString()
                                : new Date(roundModel.createdAt.seconds * 1000).toLocaleString())
                            : '-'}
                    </div>
                    <div>
                        {roundModel.completedAt
                            ? (typeof roundModel.completedAt.toDate === 'function'
                                ? roundModel.completedAt.toDate().toLocaleString()
                                : new Date(roundModel.completedAt.seconds * 1000).toLocaleString())
                            : '-'}
                    </div>
                    <div>
                        {roundModel.createdAt && roundModel.completedAt
                            ? (() => {
                                const start = typeof roundModel.createdAt.toDate === 'function'
                                    ? roundModel.createdAt.toDate()
                                    : new Date(roundModel.createdAt.seconds * 1000);
                                const end = typeof roundModel.completedAt.toDate === 'function'
                                    ? roundModel.completedAt.toDate()
                                    : new Date(roundModel.completedAt.seconds * 1000);
                                const ms = end.getTime() - start.getTime();
                                if (ms < 0) return '-';
                                const totalMinutes = Math.floor(ms / 60000);
                                const hours = Math.floor(totalMinutes / 60);
                                const minutes = totalMinutes % 60;
                                return `${hours > 0 ? hours + 'h ' : ''}${minutes}m`;
                            })()
                            : '-'}
                    </div>
                </div>
            </div>
        </>
    );
};

export default RoundScorecard; 